import type { QueryClient } from "@tanstack/react-query";

import { removeTrigger, replaceTrigger } from "../triggerList";
import type { TriggerRecord } from "../TriggerRecord";

import { triggersQueryKeys } from "./triggersQueryKeys";

/**
 * Caches `record`, a trigger the server returned from a create or an update.
 * `nameBefore` is the trigger's name before the request; it differs from
 * `record.name` only when an update renamed the trigger.
 *
 * 1. Writes `record` to the trigger's query by name and its query by id.
 * 2. After a rename, removes the query for `nameBefore`, so a later read of
 *    that name fetches from the server instead of returning this trigger.
 * 3. Replaces the trigger named `nameBefore` in the org's cached list. A list
 *    that isn't cached is left alone.
 * 4. Refetches the query by name and the list.
 *
 * The refetch cancels a fetch already in flight without rolling it back, so
 * for those two queries neither that fetch nor a later rollback (as on
 * unmount) can undo the write. The query by id is not refetched.
 */
export function storeTrigger(
  queryClient: QueryClient,
  orgId: string,
  nameBefore: string,
  record: TriggerRecord,
): Promise<unknown> {
  const byName = triggersQueryKeys.byName(orgId, record.name);
  const list = triggersQueryKeys.list(orgId);
  queryClient.setQueryData(byName, record);
  queryClient.setQueryData(
    triggersQueryKeys.byId(orgId, record.trigger_id),
    record,
  );
  if (nameBefore !== record.name) {
    queryClient.removeQueries({
      queryKey: triggersQueryKeys.byName(orgId, nameBefore),
    });
  }
  queryClient.setQueryData<readonly TriggerRecord[]>(list, (cached) =>
    cached === undefined
      ? undefined
      : replaceTrigger(cached, nameBefore, record),
  );
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: byName }),
    queryClient.invalidateQueries({ queryKey: list }),
  ]);
}

/**
 * Removes a deleted trigger from its own query and from the org's cached
 * list, then refetches the list. A list that isn't cached is left alone.
 */
export function forgetTrigger(
  queryClient: QueryClient,
  orgId: string,
  name: string,
): Promise<void> {
  const list = triggersQueryKeys.list(orgId);
  queryClient.removeQueries({
    queryKey: triggersQueryKeys.byName(orgId, name),
  });
  queryClient.setQueryData<readonly TriggerRecord[]>(list, (cached) =>
    cached === undefined ? undefined : removeTrigger(cached, name),
  );
  return queryClient.invalidateQueries({ queryKey: list });
}
